import React, { useCallback } from 'react';

import { Form, Input } from 'antd';

import { useField } from 'formik';
import type { FieldProps } from 'formik';
import { Hint } from 'v2/components/Shared/Hint';
import useMask from 'v2/hooks/useMask';

export type TextFieldProps = FieldProps & {
  mask?: string;
  variant?: 'outlined' | 'borderless' | 'filled';
  label?: string;
  helperText?: React.ReactNode;
  multiline?: boolean;
  InputProps?: any;
  InputLabelProps?: any;
  SelectProps?: any;
  FormHelperTextProps?: any;
  inputProps?: Record<string, unknown>;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  id?: string;
  onFocus?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  maxLength?: number;
  type?: string;
  autoFocus?: boolean;
};

export const TextFormField = ({
  field: fieldProp,
  form,
  helperText,
  label,
  multiline,
  InputProps,
  ...props
}: TextFieldProps) => {
  const [field, meta] = useField(fieldProp.name);
  const { name } = field;
  const { mask } = props;
  const value = field?.value || '';
  const errorText = meta.touched && meta.error;

  const { maskedValue, maskClean } = useMask(mask, value);

  const onChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      return form.setFieldValue(field.name, maskClean(e.target.value));
    },
    [form, field.name, maskClean]
  );

  const onBlurHandler = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const v = e.target.value;

      form.setFieldTouched(field.name, true);

      if (!v) return;

      form.setFieldValue(field.name, maskClean(e.target.value));
    },
    [form, field.name, maskClean]
  );

  const Component = multiline ? Input.TextArea : Input;

  return (
    <Hint inputName={field.name}>
      <Form.Item
        className='mb-0 w-full'
        label={label}
        layout='vertical'
        help={errorText || helperText}
        validateStatus={errorText ? 'error' : undefined}
      >
        <Component
          status={errorText ? 'error' : undefined}
          name={name}
          value={maskedValue}
          onChange={onChangeHandler}
          onBlur={onBlurHandler}
          rows={multiline ? InputProps?.rows || 3 : undefined}
          {...props}
        />
      </Form.Item>
    </Hint>
  );
};

export default TextFormField;
